/**
 * @fileoverview Event adapter
 *
 * Purpose:
 *  - Bridges between Morgan's `CalendarEvent` shape (wire format from
 *    `convex/keyDates/queries.ts`) and the ported big-calendar `IEvent`
 *    contract that every view component consumes.
 *  - Owns the colour map: category + status drive the badge colour so
 *    the practitioner can read intent at a glance (lodgements are
 *    amber, action deadlines red, completions go gray).
 *
 * Pure functions only — easy to test, no React deps.
 */

import type { FunctionReturnType } from "convex/server";
import type { api } from "@cvx/_generated/api";
import type { IEvent } from "@/calendar/interfaces";
import type { TEventColor } from "@/calendar/types";
import { priorityMeta } from "@/calendar/task-priority";

/** Wire shape returned by listForJob / listForOrg. */
export type CalendarEventRow = FunctionReturnType<
  typeof api.keyDates.queries.listForJob
>[number];

// ---------------------------------------------------------------------------
// Type axis (event vs task)
// ---------------------------------------------------------------------------

/**
 * Whether a row should render with the *event* treatment (timing-forward) vs
 * the *task* treatment (due + planned + PM stack). Statutory rows are always
 * events: they're events while purely derived (`isDerived`), and they stay
 * events once materialised into a `statutory_override` row (which reports
 * `isDerived: false` and is persisted with `kind: "task"` for legacy reasons)
 * — a materialised statutory row still carries its `code`, so we key off that
 * to keep materialisation transparent. `milestone` — a date anchor — is an
 * event too. Only an explicit user-created `task` kind (no `code`) gets the
 * task treatment.
 */
export function isEventKind(event: CalendarEventRow): boolean {
  return event.isDerived || Boolean(event.code) || event.kind !== "task";
}

// ---------------------------------------------------------------------------
// Colour map
// ---------------------------------------------------------------------------

/**
 * Collapse the 13-colour firm `BadgeColor` palette onto the eight-colour
 * big-calendar palette (`TEventColor`) by nearest hue.
 */
function badgeColorToEventColor(color: string | null): TEventColor {
  switch (color) {
    case "blue":
    case "sky":
    case "indigo":
      return "blue";
    case "teal":
    case "green":
    case "emerald":
      return "green";
    case "amber":
      return "yellow";
    case "orange":
      return "orange";
    case "red":
      return "red";
    case "pink":
      return "pink";
    case "purple":
    case "violet":
      return "purple";
    case "gray":
    default:
      return "gray";
  }
}

/**
 * Map a Morgan KeyDate / task to a big-calendar palette colour (MOR-696).
 *
 * Precedence: a terminal workflow status (completed/canceled) wins so done
 * work always reads neutral; then computed overdue (non-appointment) goes
 * red; then the firm status colour; then priority; then the category
 * fallback that preceded the task layer.
 */
export function colorForEvent(event: CalendarEventRow): TEventColor {
  if (event.statusType === "completed") return "green";
  if (event.statusType === "canceled") return "gray";
  if (event.overdue && event.category !== "appointment") return "red";

  if (event.statusColor) return badgeColorToEventColor(event.statusColor);

  if (event.priority && event.priority > 0) {
    return badgeColorToEventColor(priorityMeta(event.priority).color);
  }

  switch (event.category) {
    case "appointment":
      return "blue";
    case "lodgement":
      return "orange";
    case "meeting":
      return "purple";
    case "action_deadline":
      return "red";
    case "investigation_window":
      return "pink";
    case "milestone":
      return "purple";
    case "custom":
    default:
      return "gray";
  }
}

// ---------------------------------------------------------------------------
// IEvent construction
// ---------------------------------------------------------------------------

/**
 * Build ISO datetime strings for big-calendar. Critical: we emit
 * **local** (un-zoned) ISO strings so date-fns `parseISO` parses
 * them as the practitioner's wall-clock time. The previous version
 * suffixed `Z` which forced UTC midnight — in Australia (UTC+10/11)
 * that pushed every "all-day" event across two local calendar days,
 * making the week view render each one in two columns. Local
 * midnight keeps single-day events in one column regardless of
 * timezone.
 */
function toIsoBounds(event: CalendarEventRow): {
  startIso: string;
  endIso: string;
} {
  if (event.allDay) {
    const start = `${event.date}T00:00:00.000`;
    const end = event.endDate
      ? `${event.endDate}T23:59:59.999`
      : `${event.date}T23:59:59.999`;
    return { startIso: start, endIso: end };
  }
  const start = `${event.date}T${event.startTime ?? "09:00"}:00.000`;
  const end = event.endDate
    ? `${event.endDate}T${event.endTime ?? "10:00"}:00.000`
    : `${event.date}T${event.endTime ?? "10:00"}:00.000`;
  return { startIso: start, endIso: end };
}

const FALLBACK_USER = {
  id: "morgan-firm",
  name: "Morgan",
  picturePath: null,
};

/**
 * Convert one Morgan row to a big-calendar `IEvent`. Single source of
 * truth for the description string the calendar renders (we prefer
 * description over notes; both fall back to empty).
 */
export function toCalendarEvent(event: CalendarEventRow): IEvent {
  const { startIso, endIso } = toIsoBounds(event);
  return {
    id: event.id,
    startDate: startIso,
    endDate: endIso,
    title: event.title,
    color: colorForEvent(event),
    description: event.description ?? event.notes ?? "",
    user: FALLBACK_USER,
    meta: {
      persistedId: event.persistedId,
      code: event.code,
      category: event.category,
      flag: event.flag,
      overdue: event.overdue,
      kind: event.kind,
      statusId: event.statusId,
      statusName: event.statusName,
      statusType: event.statusType,
      statusColor: event.statusColor,
      priority: event.priority,
      plannedDate: event.plannedDate,
      plannedEndDate: event.plannedEndDate,
      plannedAllDay: event.plannedAllDay,
      plannedStartTime: event.plannedStartTime,
      plannedEndTime: event.plannedEndTime,
      dueDate: event.date,
      parentTaskId: event.parentTaskId,
      subtaskTotal: event.subtaskTotal,
      subtaskCompleted: event.subtaskCompleted,
      isDerived: event.isDerived,
      allDay: event.allDay,
      orgId: event.orgId,
      jobId: event.jobId,
      jobName: event.jobName,
      assigneeUserId: event.assigneeUserId,
      assignorUserId: event.assignorUserId,
      recurrenceRuleId: event.recurrenceRuleId,
      notes: event.notes,
    },
  };
}

export function toCalendarEvents(events: CalendarEventRow[]): IEvent[] {
  return events.map(toCalendarEvent);
}

// ---------------------------------------------------------------------------
// Planned vs due placement (MOR-696)
// ---------------------------------------------------------------------------

/**
 * Which date(s) the calendar places events on:
 *  - `due`     — the hard due date (default, the canonical placement)
 *  - `planned` — the soft planned window (falls back to due when unplanned)
 *  - `both`    — the due copy plus a ghost copy on the planned window
 */
export type CalendarDateMode = "due" | "planned" | "both";

/**
 * Local (un-zoned) ISO bounds for a task's planned window. When the planned
 * window is all-day we span the full day(s); when the practitioner has blocked
 * out a specific time we emit a timed slot so week/day views render it as a
 * capacity block rather than an all-day ghost.
 */
function plannedBounds(
  meta: NonNullable<IEvent["meta"]>,
): { startIso: string; endIso: string } | null {
  if (!meta.plannedDate) return null;
  const endDay = meta.plannedEndDate ?? meta.plannedDate;
  if (meta.plannedAllDay === false) {
    return {
      startIso: `${meta.plannedDate}T${meta.plannedStartTime ?? "09:00"}:00.000`,
      endIso: `${endDay}T${meta.plannedEndTime ?? "10:00"}:00.000`,
    };
  }
  return {
    startIso: `${meta.plannedDate}T00:00:00.000`,
    endIso: `${endDay}T23:59:59.999`,
  };
}

/**
 * Re-place an already-built `IEvent[]` (placed on due dates) according to the
 * header's planned/due toggle. In `both` mode, tasks with a planned window
 * that differs from their due date get a second "planned" ghost copy (a
 * distinct `id` so React keys stay unique).
 */
export function placeEventsByDateMode(
  events: IEvent[],
  mode: CalendarDateMode,
): IEvent[] {
  if (mode === "due") return events;

  const out: IEvent[] = [];
  for (const e of events) {
    const planned = e.meta ? plannedBounds(e.meta) : null;

    if (mode === "planned") {
      if (planned && e.meta) {
        out.push({
          ...e,
          startDate: planned.startIso,
          endDate: planned.endIso,
          // The placement row split keys off `meta.allDay`; a planned copy
          // must report the *planned* all-day flag so a timed plan lands in
          // the time grid rather than the all-day row.
          meta: { ...e.meta, marker: "planned", allDay: e.meta.plannedAllDay },
        });
      } else {
        // No planned date — keep the due placement so the row still shows.
        out.push(e.meta ? { ...e, meta: { ...e.meta, marker: "due" } } : e);
      }
      continue;
    }

    // both: always keep the due copy …
    out.push(e.meta ? { ...e, meta: { ...e.meta, marker: "due" } } : e);
    // … plus a planned ghost copy whenever a planned window exists. The
    // ghost renders even when planned == due (as a dashed marker beside the
    // hard due pill) so "Both" always shows everything "Planned" shows
    // (MOR-707 follow-up).
    if (planned && e.meta) {
      out.push({
        ...e,
        id: `${e.id}::planned`,
        startDate: planned.startIso,
        endDate: planned.endIso,
        meta: { ...e.meta, marker: "planned", allDay: e.meta.plannedAllDay },
      });
    }
  }
  return out;
}
